/**
 * Variable contexts in templates.
 *
 * @since 0.1.0
 */
import { interface as iface, intersection, literal, number, partial, Type, string, union } from 'io-ts';
import { Dependency } from '~/src/design/template/dependency';
import { Translations } from '~/src/model/Language';
import { NonEmpty } from '~/src/model/Boxes';

/**
 *  A real numeric value to be substituted in to the model.
 *
 * - The `Label` can be used for form identifiers etc, and may be used to decide when to
 *   show a field. They will be consistant between templates of the same type for particular
 *   purposes.
 * - The `Names` field holds the user-friendly names for this variable.
 * - The `Value` field is the numeric value to be used. It _must_ be within the range
 *   specified in`range`.
 *
 * @since 0.1.0
 */
export type Variable<T> = {
	Collate?: string,
	Rank?: number,
	Depends: Dependency,
	Label: T,
	Names: Translations,
	Range: Range,
	Value: number,
};
/**
 * Codec for `Variable`.
 *
 * @since 0.1.0
 */
export const Variable = <T>(codecT: Type<T>): Type<Variable<T>> => intersection([ partial({
	Collate: string,
	Rank: number,
}), iface({
	Depends: Dependency,
	Label: codecT,
	Names: Translations,
	Range: Range,
	Value: number,
}) ]);

/**
 * A range of numbers from `min` to `max`.
 *
 * There must exist some natural number `a`, such that `min + a * step = x`.
 *
 * @since 0.1.0
 */
export type RangeRange = {
	type: 'Range',
	Min: number,
	Max: number,
	Step: number,
};
/**
 * Codec for `RangeRange`.
 *
 * @since 0.1.0
 */
export const RangeRange: Type<RangeRange> = iface({
	type: literal('Range'),
	Min: number,
	Max: number,
	Step: number,
});

/**
 * A list of numbers that may be submitted, there will be at least one entry.
 *
 * @since 0.1.0
 */
export type RangeSet = {
	type: 'Set',
	Set: NonEmpty<number>,
};
/**
 * Codec for `RangeRange`.
 *
 * @since 0.1.0
 */
export const RangeSet: Type<RangeSet> = iface({
	type: literal('Set'),
	Set: NonEmpty(number),
});

/**
 * Constraints on values of a `Variable`.
 *
 * @since 0.1.0
 */
export type Range = RangeRange | RangeSet;
/**
 * Codec for `Range`.
 *
 * @since 0.1.0
 */
export const Range: Type<Range> = union([
	RangeRange,
	RangeSet,
]);
